/**
 * Frido ATC Upsell Popup
 *
 * Behaviour replicated from the myfrido.com PDP upsell dialog:
 *  - Opens after a successful Add to Cart of the main product (instead of the cart drawer).
 *  - Shows a cross-sell product: media gallery, variant swatches with per-variant cart
 *    quantity badges, Add To Cart that swaps into a +/- quantity stepper.
 *  - Sticky footer with live cart total, total savings badge, and
 *    "Skip & Proceed" / "Proceed" (after interacting with the upsell) going to the cart.
 *
 * All element access is resolved at event time and all listeners are document-level
 * delegations: the Horizon theme morphs / re-renders section DOM (variant updates,
 * view transitions), which would orphan any cached element references.
 */
(() => {
  if (window.__fridoXsellDrawerInit) return; // theme may re-evaluate scripts after morph
  window.__fridoXsellDrawerInit = true;

  /* ── state (lives in closure; survives DOM morphs) ───────────── */
  let cart = null;
  let selectedId = null;
  let interacted = false; // mirrors live "ATC Clicked" state: footer swaps Skip & Proceed -> Proceed
  let busy = false;
  const compareAtCache = new Map(); // variant_id -> compare_at_price (paise)

  const SELECTED_STYLE = {
    border: '1.2px solid #fcd00b',
    background: 'linear-gradient(185.23deg, #FFF5C9 -153.37%, #FFF9DF 52.38%, #FFFBEB 165.83%)',
  };

  /* ── DOM access, resolved fresh every time ───────────────────── */
  const dialog = () => document.querySelector('[data-frido-xsell-drawer]');

  function config() {
    const el = document.querySelector('[data-frido-xsell-drawer-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function ensureSelected(cfg) {
    if (selectedId && cfg.variants.some((v) => v.id === selectedId)) return;
    selectedId = (cfg.variants.find((v) => v.available) || cfg.variants[0]).id;
  }

  const formatMoney = (paise) => '₹' + Math.round(paise / 100).toLocaleString('en-IN');

  /* ── cart state ──────────────────────────────────────────────── */

  async function fetchCart() {
    cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    await hydrateCompareAt();
  }

  // /cart.js has no compare-at prices; fetch each product's .js once to compute
  // "MRP saved" the way the live site shows it. Failures degrade to discount-only savings.
  async function hydrateCompareAt() {
    const handles = [...new Set(cart.items.filter((i) => !compareAtCache.has(i.variant_id)).map((i) => i.handle))];
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const product = await (await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })).json();
          for (const v of product.variants) compareAtCache.set(v.id, v.compare_at_price || 0);
        } catch (e) {
          /* non-fatal */
        }
      })
    );
  }

  const quantityOf = (variantId) =>
    cart ? cart.items.filter((i) => i.variant_id === variantId).reduce((sum, i) => sum + i.quantity, 0) : 0;

  const lineOf = (variantId) => cart && cart.items.find((i) => i.variant_id === variantId);

  function totalSavings() {
    let saved = 0;
    for (const item of cart.items) {
      const compareAt = item.compare_at_price || compareAtCache.get(item.variant_id) || 0;
      const base = Math.max(compareAt, item.original_price) * item.quantity;
      saved += base - item.final_line_price;
    }
    return saved;
  }

  function announceCartUpdate() {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'frido-atc-upsell-popup',
          data: { source: 'frido-atc-upsell-popup', itemCount: cart.item_count },
        },
      })
    );
  }

  /* ── rendering ───────────────────────────────────────────────── */

  function render() {
    const root = dialog();
    const cfg = config();
    if (!root || !cfg || !cart) return;
    ensureSelected(cfg);

    // swatch selection + per-variant qty badges.
    // Pill shape (image = 48px circle, text = padded pill) is set in CSS via the
    // frido-swatch--image / --text classes so the first paint is already correct —
    // no square-to-round flash. JS only toggles the selected ring/background.
    for (const button of root.querySelectorAll('[data-upsell-option]')) {
      const id = Number(button.dataset.variantId);
      const isSelected = id === selectedId;
      button.style.border = isSelected ? SELECTED_STYLE.border : '';
      button.style.background = isSelected ? SELECTED_STYLE.background : '';
      const badge = root.querySelector(`[data-qty-badge="${id}"]`);
      if (badge) {
        const quantity = quantityOf(id);
        badge.hidden = quantity === 0;
        badge.firstElementChild.textContent = quantity;
      }
    }

    // media gallery for selected variant
    for (const track of root.querySelectorAll('[data-variant-media]')) {
      track.hidden = Number(track.dataset.variantMedia) !== selectedId;
    }

    // Add To Cart <-> stepper
    const quantity = quantityOf(selectedId);
    const selected = cfg.variants.find((v) => v.id === selectedId);
    const stepper = root.querySelector('[data-stepper]');
    const atc = root.querySelector('[data-upsell-atc]');
    if (stepper) stepper.hidden = quantity === 0;
    if (atc) {
      atc.hidden = quantity > 0;
      atc.disabled = busy || !selected.available;
    }
    const qtyText = root.querySelector('[data-qty-text]');
    if (qtyText) qtyText.textContent = quantity;
    const atcText = root.querySelector('[data-upsell-atc-text]');
    if (atcText) atcText.textContent = selected.available ? 'Add To Cart' : 'OUT OF STOCK';

    // footer
    const total = root.querySelector('[data-cart-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
    const saved = totalSavings();
    const badge = root.querySelector('[data-savings-badge]');
    if (badge) badge.hidden = saved <= 0;
    const savedAmount = root.querySelector('[data-cart-saved]');
    if (savedAmount) savedAmount.textContent = formatMoney(saved);
    const skip = root.querySelector('[data-upsell-skip]');
    if (skip) skip.hidden = interacted;
    const proceed = root.querySelector('[data-upsell-proceed]');
    if (proceed) proceed.hidden = !interacted;
  }

  function setStepperBusy(isBusy) {
    busy = isBusy;
    const root = dialog();
    if (!root) return;
    const loader = root.querySelector('[data-stepper-loader]');
    if (loader) loader.hidden = !isBusy;
    const row = root.querySelector('[data-stepper-row]');
    if (row) row.style.height = isBusy ? '56px' : '';
    for (const sel of ['[data-upsell-decrement]', '[data-upsell-increment]']) {
      const btn = root.querySelector(sel);
      if (btn) btn.disabled = isBusy;
    }
  }

  function setAtcBusy(isBusy) {
    busy = isBusy;
    const root = dialog();
    if (!root) return;
    const atc = root.querySelector('[data-upsell-atc]');
    if (atc) atc.disabled = isBusy;
    const loader = root.querySelector('[data-upsell-atc-loader]');
    if (loader) loader.hidden = !isBusy;
    const text = root.querySelector('[data-upsell-atc-text]');
    if (text) text.hidden = isBusy;
  }

  /* ── cart mutations ──────────────────────────────────────────── */

  async function addUpsell() {
    if (busy) return;
    setAtcBusy(true);
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: selectedId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('add failed');
      interacted = true;
      await fetchCart();
      announceCartUpdate();
    } catch (e) {
      /* leave UI unchanged on failure */
    }
    setAtcBusy(false);
    render();
  }

  async function changeQuantity(delta) {
    if (busy) return;
    const line = lineOf(selectedId);
    if (!line) return;
    setStepperBusy(true);
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: Math.max(0, line.quantity + delta) }),
      });
      if (!response.ok) throw new Error('change failed');
      cart = await response.json();
      interacted = true;
      await hydrateCompareAt();
      announceCartUpdate();
    } catch (e) {
      /* leave UI unchanged on failure */
    }
    setStepperBusy(false);
    render();
  }

  /* ── open / close ────────────────────────────────────────────── */

  // Strip auto-open and close any open cart drawer (native <dialog> and/or the
  // cart-drawer custom element) so only the ATC popup shows.
  function suppressCartDrawer() {
    for (const drawer of document.querySelectorAll('cart-drawer, cart-drawer-component')) {
      drawer.removeAttribute('auto-open');
      try { if (typeof drawer.close === 'function') drawer.close(); } catch (e) { /* ignore */ }
    }
    for (const dlg of document.querySelectorAll('dialog.cart-drawer__dialog[open], dialog[class*="cart-drawer"][open]')) {
      try { dlg.close(); } catch (e) { /* ignore */ }
    }
  }

  // A single close isn't enough: the drawer can (re)open on a later cart:update
  // that fires after the add finishes. While the popup is showing, actively keep
  // the drawer closed by re-suppressing it whenever any element toggles `open`.
  let drawerGuard = null;
  function startDrawerGuard() {
    suppressCartDrawer();
    if (drawerGuard || typeof MutationObserver === 'undefined') return;
    drawerGuard = new MutationObserver(suppressCartDrawer);
    drawerGuard.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'auto-open'],
    });
  }
  function stopDrawerGuard() {
    if (drawerGuard) { drawerGuard.disconnect(); drawerGuard = null; }
  }

  async function open() {
    // Suppress the store's cart drawer so only the popup shows. The drawer is a
    // native <dialog class="cart-drawer__dialog"> opened by cart-drawer(-component)
    // on the same cart:update — stripping auto-open alone loses the race, so we
    // also close the dialog outright, and repeat on the next frame + a short
    // timeout to catch it if it opens just after this handler runs.
    startDrawerGuard();
    // show immediately (live-site behaviour) behind a spinner; the cart data
    // (totals, savings, per-variant qty badges) fills in once /cart.js returns,
    // so the user never sees the content shift from a 0 -> real state.
    const root = dialog();
    if (!root) return;
    root.classList.add('is-loading');
    if (!root.open) {
      try {
        root.showModal();
      } catch (e) {
        /* detached or already-open edge case — next trigger re-resolves */
      }
    }
    render();
    // safety: never leave the spinner up if the cart fetch hangs
    const safety = setTimeout(() => root.classList.remove('is-loading'), 2500);
    try {
      await fetchCart();
    } catch (e) {
      /* footer totals stay as-is; popup remains usable */
    }
    clearTimeout(safety);
    render();
    root.classList.remove('is-loading');
  }

  function close() {
    stopDrawerGuard();
    const root = dialog();
    if (root && root.open) root.close();
  }

  function goToCart() {
    close();
    // open the store's cart drawer rather than navigating to the /cart page
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      try {
        drawer.open();
        return;
      } catch (e) {
        /* fall through to other triggers */
      }
    }
    // fall back to the header cart trigger (covers app-managed drawers)
    const trigger = document.querySelector(
      'button[aria-label="Cart" i], .header-actions__cart-icon, cart-icon, a[href$="/cart"]'
    );
    if (trigger) {
      trigger.click();
      return;
    }
    const cfg = config();
    window.location.href = (cfg && cfg.cartUrl) || '/cart';
  }

  /* ── wiring: document-level only, survives DOM morphs ────────── */

  for (const drawer of document.querySelectorAll('cart-drawer[auto-open], cart-drawer-component[auto-open]')) {
    drawer.removeAttribute('auto-open');
  }

  // Capture phase runs before the cart drawer's own bubble-phase cart:update
  // handler, so stripping auto-open here reliably prevents the drawer from
  // auto-opening on ATC (it was racing the popup and winning intermittently,
  // leaving the drawer open behind the popup).
  document.addEventListener('cart:update', suppressCartDrawer, true);

  // Cross-sell drawer opens ONLY on the Buy Both event dispatched by fxsfrd01
  // (the anchor is already added to cart by then). No post-ATC / network triggers.
  document.addEventListener('frido:open-xsell-drawer', () => { open(); });

  document.addEventListener('click', (event) => {
    const root = dialog();
    if (!root || !root.open) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!root.contains(target)) return;

    if (target.closest('[data-upsell-close]')) return close();
    if (target.closest('[data-upsell-skip]') || target.closest('[data-upsell-proceed]')) return goToCart();

    const option = target.closest('[data-upsell-option]');
    if (option) {
      selectedId = Number(option.dataset.variantId);
      render();
      return;
    }
    if (target.closest('[data-upsell-atc]')) return void addUpsell();
    if (target.closest('[data-upsell-increment]')) return void changeQuantity(1);
    if (target.closest('[data-upsell-decrement]')) return void changeQuantity(-1);

    // close on backdrop / outside-sheet click (overlay spans the dialog)
    if (target === root || target.hasAttribute('data-upsell-overlay')) close();
  });
})();
